import React from 'react';
import autoBind from 'react-autobind';
import QRCode from 'qrcode';
import { saveAs } from 'file-saver';

import {
  Modal,
  Button,
  Text,
  Box,
  Group,
  Stack,
  ActionIcon,
  Alert,
} from '@mantine/core';
import { IconX, IconDownload, IconAlertTriangle } from '@tabler/icons-react';

import Localization from '../../Localization.jsx';
import Config from '../../Config.jsx';

class QRCodeDialog extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this.canvasRef = React.createRef();
    // ONE code for every device. The teacher no longer picks a platform: the
    // QR carries an https URL and the page it opens does the per-OS dispatch.
    //
    // This used to be two codes, and the Android one was unscannable. It held an
    // intent:// URL, which only Chrome and cooperating WebViews understand —
    // "intent" is not an Android scheme, so an ordinary QR scanner ACTION_VIEWs
    // it, finds no handler, and says 「找不到可開啟的應用程式。」 The iOS code
    // (a bare eduventurex:// URL) had the mirror-image problem: fine in Apple's
    // Camera, dead in the in-app WebView third-party scanners use, and with no
    // answer at all for a device that has not installed the app yet.
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open && this.props.localeId) {
      setTimeout(() => this.generateQR(), 100);
    }
  }

  // The single URL this QR encodes, for every device.
  currentUrl() {
    return Config.EPACK_OPEN_URL(this.props.localeId);
  }

  generateQR() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    QRCode.toCanvas(canvas, this.currentUrl(), {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    }, (err) => {
      if (err) console.error('QR generation error:', err);
    });
  }

  handleDownload() {
    const canvas = this.canvasRef.current;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (blob) {
        const name = this.props.localeName
          ? `${this.props.localeName}_QR.png`
          : `locale_${this.props.localeId}_QR.png`;
        saveAs(blob, name);
      }
    }, 'image/png');
  }

  render() {
    const { open, onClose, localeName, isPublished, pendingUnpublish } = this.props;

    return (
      <Modal
        opened={open}
        onClose={onClose}
        size="xs"
        centered
        zIndex={2000}
        withCloseButton={false}
        title={(
          <Group justify="space-between" w="100%">
            <Text fw={600}>{Localization.strings.QR_CODE}</Text>
            <ActionIcon variant="subtle" color="gray" onClick={onClose}>
              <IconX size={18} />
            </ActionIcon>
          </Group>
        )}
      >
        <Stack gap="md" align="center">
          {localeName && (
            <Text size="sm" fw={500} ta="center">
              {localeName}
            </Text>
          )}
          {/* An unpublished LOCALE is filtered out of the app's epack list
              server-side, so the code scans correctly and the app then reports
              that the LOCALE cannot be found — which reads exactly like a broken
              QR. Say so here, where the teacher can still act on it. */}
          {isPublished === false && (
            <Alert
              variant="light"
              color="yellow"
              icon={<IconAlertTriangle size={18} />}
              p="xs"
            >
              <Text size="xs">
                {Localization.strings.QR_CODE_UNPUBLISHED_WARNING}
              </Text>
            </Alert>
          )}
          {/* The mirror image: the LOCALE is published right now, so the code
              works, but the teacher has switched Publish OFF and not yet saved.
              Pressing Update kills every code printed from this dialog, and
              nothing else in the UI connects those two acts. */}
          {pendingUnpublish === true && (
            <Alert
              variant="light"
              color="yellow"
              icon={<IconAlertTriangle size={18} />}
              p="xs"
            >
              <Text size="xs">
                {Localization.strings.QR_CODE_PENDING_UNPUBLISH_WARNING}
              </Text>
            </Alert>
          )}
          <Box
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: 4,
              padding: 16,
              backgroundColor: '#ffffff',
            }}
          >
            <canvas ref={this.canvasRef} />
          </Box>
          <Text size="xs" fw={500} ta="center">
            {Localization.strings.QR_CODE_PLATFORM_HINT}
          </Text>
          <Text size="xs" c="dimmed" ta="center">
            {Localization.strings.QR_CODE_DESC}
          </Text>
          <Text
            size="xs"
            c="dimmed"
            ta="center"
            style={{ fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: '100%' }}
          >
            {this.currentUrl()}
          </Text>
          <Group justify="center" mt="sm">
            <Button
              leftSection={<IconDownload size={18} />}
              onClick={this.handleDownload}
            >
              {Localization.strings.DOWNLOAD_QR_CODE}
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }
}

export default QRCodeDialog;
